import React, { useState } from 'react';
import { IOrder, IOrderStatus } from '@/interfaces/order';
import { DetailItem } from '@/components/dataItem/data-view-item';
import { EditFilled } from '@ant-design/icons';
import Title from '@/components/title';
import { Button, Divider, notification, Typography } from 'antd';
import { formatDate, formatNumber } from '@/utils/dataUtils';
import { getShippingFullAddress } from '@/utils/shippingAddressUtils';
import OrderItemTable from './components/order-item-table';
import OrderPrint from './components/order-print';
import { getFullAddress } from '@/utils/customerUtils';
import { EOrderStatus } from '@/enums/EOrder';
import FooterUpdate from '@/components/footer/footer-update';
import { useMutation } from '@tanstack/react-query';
import { updateOrderStatus } from '@/adapters/order';
import OrderOutStock from '../order-outstock';
import ConfirmApprove from '@/components/modal/confirm-approve';
import { IDataModal } from '@/interfaces/dataModal';
import DeleteConfirmModal from '@/components/modal/confirm-delete';
import { isFullAccess, isManager } from '@/utils/permissionUtils';
import { EShippingType } from '@/enums/EShippingType';
import { getShippingTypeName } from '@/helpers/shippingType';
import {
  isEditOrderInfo,
  isSuperUserEditOrderInfo,
} from '@/helpers/orderHelper';
import OrderPartialReturnOrder from '@/containers/order/maintain/components/order-info/components/order-partial-return-modal';
import { useNavigate } from 'react-router-dom';
import { useSetting } from '@/contexts/CompanyContext';

interface IProps {
  data: IOrder;
  onEdit: () => void;
  onRefresh: () => void;
}

const _modal: IDataModal<IOrderStatus> = {
  open: false,
};
const _returnModal: IDataModal<IOrder> = {
  open: false,
};

const OrderViewInfo = ({ data, onEdit, onRefresh }: IProps) => {
  const { setting } = useSetting();
  const navigate = useNavigate();
  const perFullAccess = isFullAccess();
  const perManager = isManager();
  const [completedOrder, setCompletedOrder] =
    useState<IDataModal<IOrderStatus>>(_modal);
  const [returnOrder, setReturnOrder] = useState<IDataModal<IOrder>>({
    ..._returnModal,
  });
  const mutations = useMutation({
    mutationFn: async (data: IOrderStatus) => await updateOrderStatus(data),
    mutationKey: ['update-order-status'],
    onSuccess: data => {
      if (data.status === 200) {
        notification.success({
          message: 'อัพเดทสถานะออเดอร์',
        });
        setCompletedOrder({
          ..._modal,
        });
        onRefresh();
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  const onUpdateStatus = (remark?: string) => {
    mutations.mutateAsync({
      orderId: Number(data.id),
      status: completedOrder.item?.status as EOrderStatus,
      deleteRemark: remark,
    });
  };

  const isShowDelete =
    ((perFullAccess &&
      isEditOrderInfo(data?.status, data?.isConfirm as boolean)) ||
      (perManager &&
        isSuperUserEditOrderInfo(
          data?.status,
          data?.isConfirm as boolean,
          data?.transportation,
          data?.isComplete,
        ))) &&
    !data?.isDelete &&
    !data?.isPod;

  const isEditOrder =
    (perFullAccess &&
      isEditOrderInfo(data?.status, data?.isConfirm as boolean)) ||
    (perManager &&
      isSuperUserEditOrderInfo(data?.status, data?.isConfirm as boolean) &&
      !data?.isDelete);
  const isCompleteOrder = data.status == EOrderStatus.RECEIVED;
  const isDispatch = [
    EOrderStatus.COMPLETED,
    EOrderStatus.POD,
    EOrderStatus.CANCEL,
  ].includes(data.status);

  console.log('data = ', data);
  return (
    <>
      <Title
        customClassName="text-lg text-blue-700"
        customContainerClassName="py-0"
        title={`รายละเอียด`}
      >
        <div className="flex flex-row gap-2">
          {isEditOrder && (
            <Button type="primary" onClick={() => onEdit()}>
              <EditFilled />
              แก้ไข
            </Button>
          )}
        </div>
      </Title>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 w-full gap-3 border p-2 py-4 rounded-lg">
        <DetailItem label="เลขที่ออเดอร์ (OR)" detail={data?.code} />
        <DetailItem
          label="วันที่ส่งสินค้า"
          detail={formatDate(data.orderDate)}
        />
        <DetailItem
          label="วันที่ครบกำหนด"
          detail={`${formatDate(data?.dueDate)} (${data.creditTerm || 0} วัน)`}
        />
        <DetailItem label="เลขที่ใบสั่งสินค้า (CO)" detail={data?.soNo} />
        <DetailItem label="เลขที่ใบแจ้งหนี้ (IV)" detail={data?.ivNo} />
        <DetailItem label="เลขที่ใบสั่งซื้อ (PO)" detail={data?.poNo} />
        <DetailItem
          label="วันที่สั่งซื้อ (PO)"
          detail={(data?.poDate && formatDate(data?.poDate)) || ''}
        />

        <DetailItem
          label="รหัสลูกค้า"
          customClass="text-blue-700"
          detail={data.customerCode}
        />
        <DetailItem
          label="ลูกค้า"
          customClass="text-blue-700"
          detail={data.customerName}
        />
        <DetailItem label="BU" detail={data?.businessUnitName} />
        <DetailItem
          label="ที่อยู่ลูกค้า"
          detail={getFullAddress(data.customer)}
        />
        <DetailItem
          label="เบอร์โทรศัพท์"
          detail={data?.customer?.phoneNumber}
        />
        <DetailItem
          label="IV REF."
          customClass="text-blue-700"
          detail={data.refIvCode}
        />
        <DetailItem
          label="พนักงานขาย"
          customClass="text-blue-700"
          detail={data.salesName}
        />
        <div className="flex gap-3">
          <DetailItem label="หมายเหตุ" detail={data?.remark} />
        </div>
      </div>
      <Title
        customClassName="text-lg text-blue-700"
        customContainerClassName="py-0"
        title={`Export Detail`}
      />
      <div className="grid md:grid-cols-2 lg:grid-cols-3 w-full gap-3 border p-2 py-4 rounded-lg">
        <DetailItem
          label="Export Type"
          detail={data?.exportType || '-'}
          customClass="uppercase"
        />
        <DetailItem
          label="Country of Origin"
          detail={data?.countryOfOrgin || '-'}
        />
        <DetailItem label="Incoterm" detail={data?.incoTerm || '-'} />
        <DetailItem
          label="ETA"
          detail={(data?.eta && formatDate(data?.eta)) || '-'}
        />
        <DetailItem label="Payment term" detail={data?.paymentTerm || '-'} />
        <DetailItem
          label="Gross Weight"
          detail={(data?.grossWeight && formatNumber(data?.grossWeight)) || '-'}
        />
        <DetailItem
          label="Net Weight"
          detail={(data?.netWeight && formatNumber(data?.netWeight)) || '-'}
        />
      </div>

      <Title
        customClassName="text-lg text-blue-700 mt-4"
        customContainerClassName="py-0"
        title={`รายการ`}
      />
      <OrderItemTable order={data} orderItems={data.orderItems || []} />
      <Title
        customClassName="text-lg text-blue-700 mt-4"
        customContainerClassName="py-0"
        title={`สถานที่จัดส่ง`}
      />
      <div className="grid md:grid-cols-2 lg:grid-cols-3 w-full gap-3 border p-2 py-4 rounded-lg">
        <DetailItem
          label="จัดส่งโดย"
          customClass="bg-yellow-300 p-0.5 rounded px-2 text-black"
          detail={getShippingTypeName(data?.transportation)}
        />
        <DetailItem
          label="สถานที่"
          detail={data?.shippingAddress?.name || ''}
        />
        <DetailItem
          label="ที่อยู่จัดส่ง"
          detail={getShippingFullAddress(data.shippingAddress)}
        />
        <DetailItem
          label="ผู้ติดต่อ"
          detail={data?.shippingAddress?.contactName}
        />
        <DetailItem
          label="เบอร์โทรศัพท์"
          detail={data?.shippingAddress?.phoneNumber}
        />
        <DetailItem
          label="Email"
          detail={`${data?.shippingAddress?.email || '-'}`}
        />
        <DetailItem
          label="หมายเหตุที่อยู่จัดส่ง"
          detail={`กำหนดส่งภายใน ${data?.dueDateTime || '-'} น. (${data?.shipToRemark || '-'})`}
          customClass="bg-red-500 p-0.5 rounded px-2 text-black"
        />
        <DetailItem
          label="ทะเบียนรถ"
          detail={
            data?.numberPlate ||
            data?.keminTruckOrders?.licencePlateNumber ||
            '-'
          }
          customClass="bg-yellow-300 p-0.5 rounded px-2 text-black"
          isMap={
            data?.status === EOrderStatus.COMPLETED &&
            data?.transportation === EShippingType.TRUCK
          }
          numberPlate={
            data?.numberPlate || data?.keminTruckOrders?.licencePlateNumber
          }
          lat={data?.shippingAddress?.lat}
          lng={data?.shippingAddress?.lng}
        />
        <DetailItem
          label="Wh Document"
          detail={data?.whDocument || '-'}
          customClass="bg-yellow-300 p-0.5 rounded px-2 text-black"
        />
        {data?.shippingAddress?.maplink && (
          <DetailItem
            label="Google Map"
            detail={
              <Typography.Link
                href={data?.shippingAddress?.maplink}
                target="_blank"
                copyable={{ text: data?.shippingAddress?.maplink }}
                className="underline text-blue-700 cursor-pointer"
              >
                {data?.shippingAddress?.maplink}
              </Typography.Link>
            }
          />
        )}
      </div>

      <Title
        customClassName="text-lg text-blue-700 mt-4"
        customContainerClassName="py-0"
        title={`หมายเหตุคลังและขนส่ง`}
      />
      <div className="border p-2 py-4 rounded-lg">
        <div className="flex flex-row gap-3 w-full">
          <DetailItem
            label="หมายเหตุคลัง"
            detail={data?.whRemark || '-'}
            customClass="bg-yellow-300 rounded px-2 text-black"
          />
        </div>
        <div className="flex flex-row gap-3 w-full mt-1">
          <DetailItem
            label="หมายเหตุขนส่ง"
            detail={data?.logisticRemark || '-'}
            customClass="bg-yellow-300 rounded px-2 text-black"
          />
        </div>
        <div className="flex flex-row gap-3 w-full mt-1">
          <DetailItem
            label="Driver App Link"
            detail={`${setting?.driverLink}/${data?.driverToken}` || '-'}
            customClass="bg-yellow-300 rounded px-2 text-black"
          />
        </div>
      </div>
      {perFullAccess && (
        <OrderOutStock
          data={data}
          onRefresh={onRefresh}
          disabled={!!data.isConfirm}
        />
      )}
      <Title
        customClassName="text-lg text-blue-700 mt-4"
        customContainerClassName="py-0"
        title={`หมายเหตุวางบิล`}
      />
      <div className="flex flex-row gap-3 w-full border p-2 py-4 rounded-lg">
        <DetailItem
          label="หมายเหตุวางบิล"
          detail={data?.billingRemark || '-'}
          customClass="bg-yellow-300 rounded px-2 text-black"
        />
      </div>

      {data.status !== EOrderStatus.CANCEL && (
        <OrderPrint data={data} afterPrint={onRefresh} />
      )}
      {data && perFullAccess && (
        <>
          <Divider className="m-0" />
          <div className="flex flex-row justify-between">
            <div>
              {isShowDelete && (
                <Button
                  type="default"
                  size="large"
                  loading={mutations.isPending}
                  className="border-red-500 text-red-700"
                  onClick={() =>
                    setCompletedOrder({
                      open: true,
                      id: data.id,
                      item: { status: EOrderStatus.CANCEL } as IOrderStatus,
                    })
                  }
                >
                  ยกเลิกออเดอร์
                </Button>
              )}
            </div>
            {isDispatch && (
              <div className="flex flex-row gap-2">
                <Button
                  loading={mutations.isPending}
                  type="primary"
                  size="large"
                  className="w-[150px]"
                  onClick={() =>
                    setReturnOrder({
                      open: true,
                      id: data.id,
                      item: data,
                    })
                  }
                >
                  คืนสินค้า
                </Button>
                <Button
                  loading={mutations.isPending}
                  type="primary"
                  ghost
                  size="large"
                  className="border-red-700 bg-red-200 text-red-700"
                  onClick={() =>
                    navigate(`/cndn-note/create?orderId=${data?.id}`)
                  }
                >
                  คืนสินค้า + ออกใบลดหนี้
                </Button>
              </div>
            )}
            {isCompleteOrder && (
              <div>
                <Button
                  loading={mutations.isPending}
                  type="primary"
                  size="large"
                  className="border-green-700 bg-green-200 text-green-700"
                  onClick={() =>
                    setCompletedOrder({
                      open: true,
                      id: data.id,
                      item: {
                        status: EOrderStatus.COMPLETED,
                      } as IOrderStatus,
                    })
                  }
                >
                  บันทึกออเดอร์สำเร็จ
                </Button>
              </div>
            )}
          </div>
          {completedOrder?.item?.status === EOrderStatus.COMPLETED && (
            <ConfirmApprove
              content={`คุณต้องการยืนยันออเดอร์สำเร็จ ใช่หรือไม่`}
              handleCancel={() => {
                setCompletedOrder({ ..._modal });
              }}
              handleOk={() => {
                onUpdateStatus();
              }}
              open={completedOrder?.open}
              title={`ออเดอร์ '${data?.code}'`}
            />
          )}
          {completedOrder?.item?.status === EOrderStatus.CANCEL && (
            <DeleteConfirmModal
              content={`คุณต้องการยกเลิกออเดอร์ ใช่หรือไม่`}
              handleCancel={() => {
                setCompletedOrder({ ..._modal });
              }}
              handleOk={remark => {
                onUpdateStatus(remark);
              }}
              isRemark
              open={completedOrder?.open}
              title={`ออเดอร์ '${data?.code}'`}
            />
          )}
          {returnOrder?.open && (
            <OrderPartialReturnOrder
              dataModal={returnOrder}
              handleOk={function (): void {
                setReturnOrder({ ..._returnModal });
              }}
              handleCancel={function (): void {
                setReturnOrder({ ..._returnModal });
              }}
            />
          )}
        </>
      )}
      {data && (
        <>
          <Divider className="m-0" />
          <div className="flex flex-col md:flex-row gap-2 w-full overflow-y-auto">
            <FooterUpdate
              actionBy={data.createByName}
              actionDate={data.createDate}
              title="สร้าง"
              status={EOrderStatus.PENDING}
            />
            {data.approveByName && (
              <FooterUpdate
                actionBy={data.approveByName}
                actionDate={data.approveDate}
                title="ยืนยันจัดสินค้า"
                status={EOrderStatus.APPROVED}
              />
            )}
            {data.rejectByName && (
              <FooterUpdate
                actionBy={data.rejectByName}
                actionDate={data.rejectDate}
                title="Failed By Pioneer"
                status={EOrderStatus.REJECTED}
              />
            )}
            {data.receiveByName && (
              <FooterUpdate
                actionBy={data.receiveByName}
                actionDate={data.receiveDate}
                title="Received By Pioneer"
                status={EOrderStatus.RECEIVED}
              />
            )}
            {data.isConfirm && (
              <FooterUpdate
                actionBy={data.confirmByName}
                actionDate={data.confirmDate}
                title="ยืนยันส่งเอกสาร"
                status={EOrderStatus.CONFIRM}
              />
            )}
            {data.completeByName && (
              <FooterUpdate
                actionBy={data.completeByName}
                actionDate={data.completeDate}
                title={
                  data.transportation === EShippingType.PUIS
                    ? 'เสร็จสิ้น'
                    : 'Dispatched'
                }
                status={EOrderStatus.COMPLETED}
                transport={data.transportation}
              />
            )}
            {data.isPod && (
              <FooterUpdate
                actionBy={data.podByName}
                actionDate={data.podDate}
                title="POD"
                status={EOrderStatus.POD}
              />
            )}
            {data.deleteByName && (
              <FooterUpdate
                actionBy={data.deleteByName}
                actionDate={data.deleteDate}
                title="ยกเลิก"
                status={EOrderStatus.CANCEL}
              />
            )}
          </div>
        </>
      )}
    </>
  );
};

export default OrderViewInfo;
